import * as React from "react";
import { css, keyframes } from "@emotion/react";

import { Alert, Animation } from "@zapier/design-system";

import { Notification as NotificationType } from "../types";
import useTimer from "./useTimer";

type Props = {
  index: number;
  notification: NotificationType;
  onDismiss: (id: string) => void;
  timeout: number;
};

const openAnimation = keyframes({
  "0%": {
    opacity: 0,
    transform: "translateY(-10px)",
  },

  "100%": {
    opacity: 1,
    transform: "translateY(0)",
  },
  // This "delay" gives enough time for the menu to be measured
  // by `useIntersect` and flip upwards if it needs to, without
  // flickering for a moment while it renders down and is measured.
  "30%": {
    opacity: 0,
    transform: "translateY(-10px)",
  },
});

const rootStyle = css`
  animation: ${Animation.transitionDuration} ease-out ${openAnimation};
  max-width: 90vw;
  width: 700px;
`;

/**
 * A popup toast-style notification
 */
function Notification({ notification, onDismiss, timeout }: Props) {
  const handleDismiss = React.useCallback(() => {
    if (notification.onDismiss) {
      notification.onDismiss(notification);
    }

    onDismiss(notification.id);
  }, [notification, onDismiss]);

  const handleTimeout = React.useCallback(() => {
    // Allow the notification to be dismissed manually
    if (timeout <= 0 || timeout === Infinity) {
      return;
    }

    handleDismiss();
  }, [handleDismiss, timeout]);

  const { onPause, onResume } = useTimer(handleTimeout, timeout);

  return (
    <div css={rootStyle} onMouseEnter={onPause} onMouseLeave={onResume}>
      <Alert onDismiss={handleDismiss} status={notification.type}>
        {notification.message}
      </Alert>
    </div>
  );
}

export default Notification;
